import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useCompleteGenieCalendarConnect } from "@/hooks/agent/useGenieMeetings";
import { CalendarProvider } from "@/types/agent/genie-meeting";

const PROVIDER_NAMES: Record<CalendarProvider, string> = { google: "Google", microsoft: "Microsoft" };

// Microsoft tenants that only let users consent to some apps: AADSTS65001 (no consent yet), AADSTS90094 (admin approval).
const needsAdminApproval = (error: string | null, description: string | null): boolean =>
  error === "consent_required" || /AADSTS(65001|90094)/.test(description ?? "");

/** The genie the sign-in was started for, read from the state for the way back; the server verifies it. */
const genieIdFromState = (state: string | null): string | null => {
  try {
    const payload = state?.split(".")[0];
    if (!payload) return null;
    const { a } = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof a === "string" ? a : null;
  } catch {
    return null;
  }
};

interface CalendarCallbackProps {
  provider: CalendarProvider;
}

/** Where Google or Microsoft send the user back after the calendar sign-in. */
const CalendarCallback = ({ provider }: CalendarCallbackProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const completeMutation = useCompleteGenieCalendarConnect();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const hasProcessed = useRef(false);

  const state = searchParams.get("state");
  const genieId = genieIdFromState(state);
  const genieUrl = genieId ? `/genie-hub/my-genies/${genieId}` : "/genie-hub/my-genies";

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const code = searchParams.get("code");
    const error = searchParams.get("error");

    if (error || !code || !state) {
      setStatus("error");
      setErrorMessage(
        needsAdminApproval(error, searchParams.get("error_description"))
          ? "Your organisation's IT admin has to approve Help Genie before its calendars can be connected. Ask them to approve it, then connect again."
          : error === "access_denied"
            ? "The calendar wasn't shared, so nothing was connected."
            : `${PROVIDER_NAMES[provider]} didn't send back what's needed to connect the calendar.`,
      );
      return;
    }

    completeMutation
      .mutateAsync({ code, state })
      .then(({ agentId, connection }) => {
        setStatus("success");
        toast.success(`Calendar connected${connection.email ? `: ${connection.email}` : ""}`);
        setTimeout(() => navigate(`/genie-hub/my-genies/${agentId}`), 1500);
      })
      .catch((err: unknown) => {
        setStatus("error");
        setErrorMessage(err instanceof Error ? err.message : "The calendar couldn't be connected.");
      });
  }, [searchParams, state, navigate, completeMutation, provider]);

  return (
    <div className="min-h-[100dvh] bg-[#F9FAFC] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {status === "loading" && <Loader2 className="h-5 w-5 animate-spin" />}
            {status === "success" && <CheckCircle className="h-5 w-5 text-green-600" />}
            {status === "error" && <XCircle className="h-5 w-5 text-red-600" />}
            {status === "loading" && "Connecting your calendar..."}
            {status === "success" && "Calendar connected"}
            {status === "error" && "The calendar wasn't connected"}
          </CardTitle>
          <CardDescription>
            {status === "loading" && "Setting up which meetings your genie joins"}
            {status === "success" && "Your genie will join your calendar's meetings from now on"}
            {status === "error" && "Nothing changed on your calendar"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status === "loading" && (
            <div className="flex flex-col items-center gap-4 py-4">
              <Loader2 className="h-12 w-12 animate-spin text-genie-primary" />
            </div>
          )}

          {status === "success" && (
            <div className="flex flex-col items-center gap-4 py-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
              <p className="text-sm text-muted-foreground text-center">Taking you back to your genie...</p>
            </div>
          )}

          {status === "error" && (
            <div className="flex flex-col gap-4 py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
              <Button onClick={() => navigate(genieUrl)} className="w-full min-h-[44px]">
                Back to your genie
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarCallback;
